// The Admin hub (More → Admin): grouped tool cards + feature switches. It stays
// open underneath any subtool it launches, so closing a tool always lands back
// here — navigation comes from stacking, not per-tool back buttons.
import { useState } from 'react';
import { api } from './api';
import { Icon } from './icons';

const FEATURES: { key: string; label: string; desc: string }[] = [
  { key: 'kudos', label: 'Kudos', desc: 'Public shout-outs between teammates' },
  { key: 'jira', label: 'Jira', desc: 'Jira issues panel and linking' },
  { key: 'polls', label: 'Polls', desc: '/poll in the composer' },
  { key: 'incidents', label: 'Incidents', desc: '/incident declare-and-track flow' },
  { key: 'forum', label: 'Forum mode', desc: 'Channels can switch to a topic board layout' },
  { key: 'embeds', label: 'Embed widgets', desc: 'Website chat widget for your site' },
];

function ToolCard({ icon, label, desc, onClick }: { icon: string; label: string; desc: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left',
        padding: '14px 14px 12px', border: '1px solid var(--fx-hair)', borderRadius: 12,
        background: 'transparent', color: 'var(--fx-text)', cursor: 'pointer', font: 'inherit',
        minWidth: 0,
      }}
    >
      <span style={{
        width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 9, color: 'var(--fx-accent)', background: 'var(--fx-accent-soft, rgba(91,108,255,0.12))',
      }}>
        <Icon name={icon} size={17} />
      </span>
      <span style={{ fontWeight: 650, fontSize: 14 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--fx-muted)', lineHeight: 1.45 }}>{desc}</span>
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ margin: '0 0 8px', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fx-muted)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export function AdminPanel({
  onClose,
  onBots,
  onCompliance,
  onWebhooks,
  onWidget,
  onFields,
  workspaceId,
  features,
  onFeaturesChanged,
}: {
  onClose: () => void;
  onBots: () => void;
  onCompliance: () => void;
  onWebhooks: () => void;
  onWidget: () => void;
  onFields: () => void;
  workspaceId?: string | null;
  features?: Record<string, boolean>;
  onFeaturesChanged?: (f: Record<string, boolean>) => void;
}) {
  const [feat, setFeat] = useState<Record<string, boolean>>(features || {});
  const toggleFeature = async (key: string) => {
    if (!workspaceId) return;
    const next = { ...feat, [key]: !feat[key] };
    setFeat(next);
    try {
      const r = await api.setWorkspaceFeatures(workspaceId, { [key]: next[key] });
      setFeat(r.features);
      onFeaturesChanged?.(r.features);
    } catch { setFeat(feat); }
  };
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(620px, 94vw)', maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <div className="fx-login-title" style={{ marginBottom: 2 }}>Admin</div>
          <div style={{ fontSize: 12.5, color: 'var(--fx-muted)' }}>Workspace administration — tools open on top; close one to come back here.</div>
        </div>

        <Section title="Agents & integrations">
          <div style={grid}>
            <ToolCard icon="bolt" label="Bots & apps" desc="Agents, avatars, tokens & scopes; connect Hermes / OpenClaw" onClick={onBots} />
            <ToolCard icon="link" label="Webhooks" desc="Incoming (external → channel) & outgoing (signed) integrations" onClick={onWebhooks} />
            <ToolCard icon="screen" label="Website chat widget" desc="Embed a live chat widget on your website" onClick={onWidget} />
          </div>
        </Section>

        <Section title="People & policy">
          <div style={grid}>
            <ToolCard icon="tasks" label="Member fields & tags" desc="Directory fields members fill in — department, skills, projects…" onClick={onFields} />
            <ToolCard icon="shield" label="Admin & compliance" desc="Data-loss prevention, retention, random-coffee" onClick={onCompliance} />
          </div>
        </Section>

        <Section title="Optional features">
          <div style={{ fontSize: 12.5, color: 'var(--fx-faint)', margin: '-2px 0 8px' }}>
            Off by default — the workspace stays minimal until you need these.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {FEATURES.map((f) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 2px', borderBottom: '1px solid var(--fx-hair)' }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, color: 'var(--fx-text)', fontWeight: 600 }}>{f.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--fx-muted)' }}>{f.desc}</span>
                </span>
                <button
                  type="button"
                  className={`fx-switch${feat[f.key] ? ' is-on' : ''}`}
                  onClick={() => toggleFeature(f.key)}
                  aria-pressed={!!feat[f.key]}
                  aria-label={`${f.label} ${feat[f.key] ? 'on' : 'off'}`}
                >
                  <span className="fx-switch-knob" />
                </button>
              </div>
            ))}
          </div>
        </Section>

        <div className="fx-modal-actions" style={{ marginTop: 0 }}>
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
